import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cx } from "../../lib/cx";

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: the button has no text, so screen readers need this. */
  label: string;
  children: ReactNode;
}

/**
 * A square control that holds a single icon. `label` is mandatory and maps to
 * both aria-label and the native tooltip so the icon is never the only carrier
 * of meaning.
 */
export function IconButton({ label, children, className, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        "inline-flex h-9 w-9 items-center justify-center rounded-lg",
        "text-text-secondary transition-colors",
        // Accent used pointwise on the interactive state, per the brief.
        "hover:bg-accent-subtle hover:text-accent",
        "disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:bg-transparent disabled:hover:text-text-disabled",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
